import { useState } from "react";
import {
    Box, Button, Checkbox, CircularProgress, FormControlLabel,
    Stack, TextField, Typography,
} from "@mui/material";
import { useAuth } from "src/context/AuthContext";
import { useT } from "src/i18n";
import { patchMePerson } from "src/utils/quote/quoteApi";
import { parseFiAddress } from "src/utils/quote/quoteAddress";
import { captureError } from "src/utils/errorReporting";
import { useFieldSetter } from "src/utils/useFieldSetter";
import GlassDialog from "src/components/common/GlassDialog";

// Edits the caller's own person fields + the billing address on their typeId=2
// private asiakas. Backend writes both in one transaction; if no private asiakas
// exists yet, it's created on demand the first time an address field is saved.
//
// Prefill: person fields come from the auth context, address fields from the
// caller's typeId=2 row in the asiakases list (if any). The dialog is purely
// controlled — open/close lives in the parent (RequestForm).
//
// The parent is expected to mount this conditionally (only when open) so the
// component remounts on each open and useState re-seeds — avoids a
// setState-in-effect re-seed pattern.
//
// Props:
//   open, onClose
//   person       — { firstName, lastName, phone, email } from useCurrentPerson
//   privateAsiakas — caller's typeId=2 row from the asiakases list, or null
//   siteAddress  — worksite address string from the wizard draft (pass
//                  draft.formattedAddress only, not draft.osoite — see
//                  RequestForm comment). When non-null, the dialog renders a
//                  "Sama kuin työmaan osoite" checkbox that fills the three
//                  billing fields (parsed via parseFiAddress) and disables
//                  them. Unchecking re-enables editing without auto-revert.
//   onSaved      — called with the PATCH response after a successful save so
//                  the parent can refetch its asiakases list (the response
//                  carries the updated/new privateAsiakas row).
export default function PersonEditDialog({ open, onClose, person, privateAsiakas, siteAddress, onSaved }) {
    const { token, applyPersonUpdate } = useAuth();
    const t = useT();

    const [form, setForm] = useState(() => buildInitial(person, privateAsiakas));
    const [errors, setErrors] = useState({});
    const [status, setStatus] = useState("idle"); // idle | submitting | error
    const [submitError, setSubmitError] = useState(null);

    const update = useFieldSetter(setForm, errors, setErrors);

    function toggleUseSiteAddress(e) {
        const next = e.target.checked;
        const parsed = next ? parseFiAddress(siteAddress) : null;
        setForm((f) => ({
            ...f,
            useSiteAddress: next,
            ...(parsed && {
                laskutusOsoite: parsed.street,
                laskutusPostinumero: parsed.postal,
                laskutusKaupunki: parsed.city,
            }),
        }));
    }

    function validate(f) {
        const errs = {};
        if (!f.etunimi.trim()) errs.etunimi = t("auth.shared.etunimiRequired");
        if (!f.sukunimi.trim()) errs.sukunimi = t("auth.shared.sukunimiRequired");
        if (!f.puhelin.trim()) errs.puhelin = t("auth.shared.puhelinRequired");
        if (!f.email.trim()) errs.email = t("auth.person.emailRequired");
        else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(f.email.trim())) errs.email = t("auth.person.emailInvalid");
        return errs;
    }

    async function handleSubmit(e) {
        e.preventDefault();
        const v = validate(form);
        setErrors(v);
        if (Object.keys(v).length > 0) return;

        setStatus("submitting");
        setSubmitError(null);
        try {
            const result = await patchMePerson({
                token,
                fields: {
                    personFirstName: form.etunimi.trim(),
                    personLastName: form.sukunimi.trim(),
                    personPhone: form.puhelin.trim(),
                    personEmail: form.email.trim(),
                    laskutusOsoite: form.laskutusOsoite.trim() || null,
                    laskutusPostinumero: form.laskutusPostinumero.trim() || null,
                    laskutusKaupunki: form.laskutusKaupunki.trim() || null,
                },
            });
            if (result?.person) applyPersonUpdate(result.person);
            onSaved?.(result);
            onClose();
        } catch (err) {
            if (err?.status === 409 && err?.body?.conflict === "email") {
                setErrors((prev) => ({ ...prev, email: t("auth.person.emailConflict") }));
                setStatus("error");
                return;
            }
            captureError(err, { tags: { entity: "person", operation: "patch-me" } });
            setSubmitError(err?.message || t("auth.shared.saveFailed"));
            setStatus("error");
        }
    }

    return (
        <GlassDialog open={open} onClose={onClose} busy={status === "submitting"} title={t("auth.person.editTitle")}>
            <Box component="form" onSubmit={handleSubmit} noValidate>
                <Stack spacing={1.75}>
                    <Stack direction={{ xs: "column", sm: "row" }} spacing={1.5}>
                        <TextField
                            label={t("auth.shared.etunimiLabel")}
                            fullWidth
                            value={form.etunimi}
                            onChange={update("etunimi")}
                            error={!!errors.etunimi}
                            helperText={errors.etunimi || " "}
                            autoComplete="given-name"
                        />
                        <TextField
                            label={t("auth.shared.sukunimiLabel")}
                            fullWidth
                            value={form.sukunimi}
                            onChange={update("sukunimi")}
                            error={!!errors.sukunimi}
                            helperText={errors.sukunimi || " "}
                            autoComplete="family-name"
                        />
                    </Stack>
                    <TextField
                        label={t("auth.shared.puhelinLabel")}
                        fullWidth
                        value={form.puhelin}
                        onChange={update("puhelin")}
                        error={!!errors.puhelin}
                        helperText={errors.puhelin || " "}
                        autoComplete="tel"
                    />
                    <TextField
                        label={t("auth.shared.emailLabel")}
                        type="email"
                        fullWidth
                        value={form.email}
                        onChange={update("email")}
                        error={!!errors.email}
                        helperText={errors.email || " "}
                        autoComplete="email"
                    />

                    <Typography
                        variant="caption"
                        sx={{ color: "text.secondary", pt: 0.5, fontSize: 12 }}
                    >
                        {t("auth.person.billingAddressLabel")}
                    </Typography>
                    {siteAddress && (
                        <FormControlLabel
                            control={
                                <Checkbox
                                    checked={form.useSiteAddress}
                                    onChange={toggleUseSiteAddress}
                                    size="small"
                                />
                            }
                            label={
                                <Typography variant="body2" sx={{ fontSize: 13 }}>
                                    {t("auth.person.useSiteAddressLabel")}
                                </Typography>
                            }
                            sx={{ mr: 0, mt: -0.5 }}
                        />
                    )}
                    <TextField
                        label={t("auth.shared.streetAddressLabel")}
                        fullWidth
                        value={form.laskutusOsoite}
                        onChange={update("laskutusOsoite")}
                        autoComplete="street-address"
                        disabled={form.useSiteAddress}
                        helperText=" "
                    />
                    <Stack direction={{ xs: "column", sm: "row" }} spacing={1.5}>
                        <TextField
                            label={t("auth.shared.postalCodeLabel")}
                            value={form.laskutusPostinumero}
                            onChange={update("laskutusPostinumero")}
                            autoComplete="postal-code"
                            disabled={form.useSiteAddress}
                            sx={{ flex: { xs: "1 1 auto", sm: "0 0 40%" } }}
                            helperText=" "
                        />
                        <TextField
                            label={t("auth.shared.cityLabel")}
                            fullWidth
                            value={form.laskutusKaupunki}
                            onChange={update("laskutusKaupunki")}
                            autoComplete="address-level2"
                            disabled={form.useSiteAddress}
                            helperText=" "
                        />
                    </Stack>

                    {submitError && (
                        <Typography variant="caption" color="error" sx={{ display: "block" }}>
                            {submitError}
                        </Typography>
                    )}

                    <Stack direction="row" spacing={1} sx={{ pt: 1, justifyContent: "flex-end" }}>
                        <Button onClick={onClose} disabled={status === "submitting"}>
                            {t("auth.shared.cancel")}
                        </Button>
                        <Button
                            type="submit"
                            variant="contained"
                            disabled={status === "submitting"}
                            startIcon={status === "submitting" ? <CircularProgress size={16} color="inherit" /> : null}
                        >
                            {status === "submitting" ? t("auth.shared.saving") : t("auth.shared.save")}
                        </Button>
                    </Stack>
                </Stack>
            </Box>
        </GlassDialog>
    );
}

function buildInitial(person, privateAsiakas) {
    return {
        etunimi: person?.firstName ?? "",
        sukunimi: person?.lastName ?? "",
        puhelin: person?.phone ?? "",
        email: person?.email ?? "",
        laskutusOsoite: privateAsiakas?.laskutusOsoite ?? "",
        laskutusPostinumero: privateAsiakas?.laskutusPostinumero ?? "",
        laskutusKaupunki: privateAsiakas?.laskutusKaupunki ?? "",
        useSiteAddress: false,
    };
}
